import { graphqlQuery } from "@/lib/api/graphql";
import type { GraphQLCursorConnection } from "@/lib/graphql-pagination";
import { useCursorPagination } from "./useCursorPagination";

// --- GraphQL types ---

interface CollectionStrategyAuditLogGraphQLNode {
	auditLogId: number;
	collectionStrategyId: number;
	actionType: string | null;
	description: string;
	fieldName: string | null;
	oldValue: string | null;
	newValue: string | null;
	runId: number | null;
	metadata: string | null;
	createdByUserName: string | null;
	createdAt: string;
}

interface CollectionStrategyAuditLogListGraphQLResponse {
	collectionStrategies: {
		collectionStrategyAuditLogList: GraphQLCursorConnection<CollectionStrategyAuditLogGraphQLNode>;
	};
}

// --- GraphQL query ---

const COLLECTION_STRATEGY_AUDIT_LOG_LIST_QUERY = `
  query CollectionStrategyAuditLogList($first: Int, $after: String, $filter: CollectionStrategyAuditLogListFilterInput) {
    collectionStrategies {
      collectionStrategyAuditLogList(first: $first, after: $after, filter: $filter) {
        edges {
          node {
            auditLogId
            collectionStrategyId
            actionType
            description
            fieldName
            oldValue
            newValue
            runId
            metadata
            createdByUserName
            createdAt
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
`;

const AUDIT_LOG_PAGE_SIZE = 25;

// --- Domain type ---

export interface CollectionStrategyAuditLog {
	id: string;
	collectionStrategyId: number;
	actionType: string;
	description: string;
	fieldName?: string;
	oldValue?: string;
	newValue?: string;
	runId?: number;
	metadata?: string;
	createdBy: string;
	createdAt: Date;
}

// --- Mapping ---

function mapGraphQLNodeToAuditLog(
	node: CollectionStrategyAuditLogGraphQLNode,
): CollectionStrategyAuditLog {
	return {
		id: String(node.auditLogId),
		collectionStrategyId: node.collectionStrategyId,
		actionType: node.actionType ?? "Unknown",
		description: node.description,
		fieldName: node.fieldName || undefined,
		oldValue: node.oldValue || undefined,
		newValue: node.newValue || undefined,
		runId: node.runId ?? undefined,
		metadata: node.metadata || undefined,
		createdBy: node.createdByUserName || "System",
		createdAt: new Date(node.createdAt),
	};
}

// --- Mock data ---

function buildMockAuditLogs(strategyId: number): CollectionStrategyAuditLog[] {
	return [
		{
			id: "audit-1",
			collectionStrategyId: strategyId,
			actionType: "StrategyCreated",
			description: "Strategy created",
			createdBy: "System",
			createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
		},
		{
			id: "audit-2",
			collectionStrategyId: strategyId,
			actionType: "FieldChanged",
			description: "Name changed",
			fieldName: "Name",
			oldValue: "Old Strategy Name",
			newValue: "New Strategy Name",
			createdBy: "Sarah Johnson",
			createdAt: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000),
		},
		{
			id: "audit-3",
			collectionStrategyId: strategyId,
			actionType: "StatusChanged",
			description: "Status changed from Inactive to Active",
			fieldName: "Status",
			oldValue: "Inactive",
			newValue: "Active",
			createdBy: "Mike Chen",
			createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000),
		},
		{
			id: "audit-4",
			collectionStrategyId: strategyId,
			actionType: "RunNowTriggered",
			description: "Manual run triggered (Run #101)",
			runId: 101,
			createdBy: "Sarah Johnson",
			createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
		},
		{
			id: "audit-5",
			collectionStrategyId: strategyId,
			actionType: "ScheduledRunCompleted",
			description: "Scheduled run completed (Run #101)",
			runId: 101,
			createdBy: "System",
			createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000 + 60000),
		},
	];
}

// --- Hook ---

export interface CollectionStrategyAuditLogsGraphQLResult {
	auditLogs: CollectionStrategyAuditLog[];
	isLoading: boolean;
	isFetchingMore: boolean;
	hasMore: boolean;
	loadMore: () => void;
	reset: () => void;
}

export function useCollectionStrategyAuditLogsGraphQL(
	strategyId: string | number | null | undefined,
): CollectionStrategyAuditLogsGraphQLResult {
	const numericId = strategyId ? Number(strategyId) : null;

	const result = useCursorPagination<CollectionStrategyAuditLogGraphQLNode, CollectionStrategyAuditLog>(
		{
			pageSize: AUDIT_LOG_PAGE_SIZE,
			buildMockData: () =>
				numericId ? buildMockAuditLogs(numericId) : [],
			executeQuery: async (apiConfig, afterCursor, signal) => {
				if (!numericId) {
					return { edges: [], pageInfo: { endCursor: null, hasNextPage: false } };
				}
				const variables: Record<string, unknown> = {
					first: AUDIT_LOG_PAGE_SIZE,
					filter: { collectionStrategyId: { eq: numericId } },
				};
				if (afterCursor) {
					variables.after = afterCursor;
				}
				const response = await graphqlQuery<CollectionStrategyAuditLogListGraphQLResponse>(
					apiConfig,
					COLLECTION_STRATEGY_AUDIT_LOG_LIST_QUERY,
					variables,
					signal,
				);
				return response.collectionStrategies.collectionStrategyAuditLogList;
			},
			mapNode: mapGraphQLNodeToAuditLog,
		},
		[numericId],
	);

	return {
		auditLogs: result.items,
		isLoading: result.isLoading,
		isFetchingMore: result.isFetchingMore,
		hasMore: result.hasMore,
		loadMore: result.loadMore,
		reset: result.reset,
	};
}
